import { TableCellComponentProps } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { Input } from '@/modules/Input';
import { useState, useEffect, useRef } from 'react';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';

export const TextFieldCell = ({
  value,
  column,
  state = 'default',
  onChange,
  className,
  onCellFocus,
  stickyLeft = 0,
  isLastLocked = false,
  isRowEditing,
  hasColumnMaxWidthAuto = false,
}: TableCellComponentProps) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [validationError, setValidationError] = useState<string>('');
  const initialValueRef = useRef(value);
  const hasInteractedRef = useRef(false);
  // Detectar si está en modo edición (si el estado es 'selected' y la columna es editable)
  const isEditing = state === 'selected' && column.editable && column.editable !== 'none';
  const baseClasses = getTableCellClasses(
    state,
    className,
    column.locked,
    isLastLocked,
    isEditing,
    isRowEditing,
    false,
    column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto),
  );
  const cellProps = getTableCellProps(column, stickyLeft, undefined, false, hasColumnMaxWidthAuto);

  const hasFixedWidth = column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto);
  const alignClass = getContentAlignClass(column.contentAlign);

  // Función de validación basada en el tipo de columna
  const validateValue = (val: string): string => {
    const columnType = column.type;

    // Validar según el tipo de columna
    if (columnType === 'number' || columnType === 'numeric') {
      // Para números: debe ser un número válido (puede incluir decimales y negativos)
      if (val !== '' && val !== '-' && val !== '+' && val !== '.') {
        // Permitir números, decimales, signos negativos/positivos
        // Permitir valores parciales como "12.", "-", "+", etc. mientras se escribe
        const numberRegex = /^-?\d*\.?\d*$/;
        if (!numberRegex.test(val)) {
          return 'Debe ser un número válido';
        }
      }
    } else if (columnType === 'default' || columnType === 'textfield' || !columnType) {
      // Para texto: validación básica (puede estar vacío)
      // No hay validación específica para texto
    }

    return '';
  };

  // Sincronizar el valor cuando cambie desde fuera
  useEffect(() => {
    setCurrentValue(value);
    initialValueRef.current = value;
    hasInteractedRef.current = false;
    setValidationError('');
  }, [value]);

  const handleFocus = () => {
    // Focus handler - puede agregar lógica adicional si es necesario
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const columnType = column.type;

    // Para columnas numéricas, prevenir caracteres no numéricos
    if (columnType === 'number' || columnType === 'numeric') {
      // Permitir teclas de control (backspace, delete, tab, arrow keys, etc.)
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      // Permitir teclas especiales
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Enter',
        'Escape',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ];

      if (allowedKeys.includes(e.key)) {
        return;
      }

      // Permitir números, punto decimal, y signos negativo/positivo
      const validKeyRegex = /^[0-9.\-+]$/;
      if (!validKeyRegex.test(e.key)) {
        e.preventDefault();
        setValidationError('Solo se permiten números');
        return;
      }

      // Validar que no se pueda escribir múltiples puntos o signos
      const currentVal = currentValue?.toString() || '';
      if (e.key === '.' && currentVal.includes('.')) {
        e.preventDefault();
        setValidationError('Solo se permite un punto decimal');
        return;
      }

      if ((e.key === '-' || e.key === '+') && currentVal.length > 0) {
        e.preventDefault();
        setValidationError('El signo solo puede ir al inicio');
        return;
      }

      // Si llegamos aquí, el carácter es válido
      setValidationError('');
    }
  };

  const handleChange = (newValue: string) => {
    // Validar el nuevo valor
    const error = validateValue(newValue);
    setValidationError(error);

    // Actualizar el valor (la validación en handleKeyDown ya previno caracteres inválidos)
    setCurrentValue(newValue);
    if (!error) {
      hasInteractedRef.current = true;
    }
  };

  const handleBlur = () => {
    // Solo disparar onChange si:
    // 1. El usuario interactuó con el input (escribió algo)
    // 2. El valor realmente cambió
    if (hasInteractedRef.current && currentValue !== initialValueRef.current) {
      onChange?.(currentValue);
      initialValueRef.current = currentValue;
    }
    hasInteractedRef.current = false;
  };

  const hasError = validationError !== '';
  const errorCursor = hasError;

  return (
    <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
      <div
        className={cn(
          'ui-flex ui-h-full ui-items-center',
          alignClass,
          hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden',
        )}
      >
        <Input
          value={currentValue ? String(currentValue) : ''}
          onValueChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="sm"
          disabled={state === 'disabled'}
          fullWidth
          errorCursor={errorCursor}
        />
      </div>
    </td>
  );
};
